import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiWithAuth } from '../../lib/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  XMarkIcon,
  BuildingOfficeIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const MutedStores = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [mutedStores, setMutedStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [savingIds, setSavingIds] = useState(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [serverCap, setServerCap] = useState({ search: false, pagination: false });
  const debounceTimer = useRef(null);

  useEffect(() => {
    fetchMutedStores({ page: 1, q: '' });
  }, []);

  const fetchMutedStores = async ({ page: pg = 1, q = searchTerm }) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      const candidates = ['/users/muted-stores', '/user/muted-stores', '/muted-stores'];
      let data = null;
      let meta = { total: 0 };
      let caps = { search: false, pagination: false };
      for (const path of candidates) {
        try {
          const params = {};
          if (q && q.trim()) { params.q = q.trim(); caps.search = true; }
          if (pg && pageSize) { params.page = pg; params.page_size = pageSize; caps.pagination = true; }
          const res = await client.get(path, { params });
          const d = res?.data;
          const arr = Array.isArray(d) ? d : (Array.isArray(d?.results) ? d.results : (Array.isArray(d?.data) ? d.data : null));
          if (arr) {
            data = arr;
            const t = (typeof d?.total === 'number') ? d.total : (typeof d?.count === 'number' ? d.count : arr.length);
            meta.total = t;
            break;
          }
        } catch (_) { /* try next */ }
      }
      // 正規化
      const normalized = (Array.isArray(data) ? data : []).map((s, idx) => ({
        id: s.id || s.employer_id || String(idx),
        employer_id: s.employer_id,
        employer_name: s.employer_name || s.name || '店舗',
        reason: s.reason || 'other',
        reason_text: s.reason_text || 'ミュート',
        muted_date: s.muted_date || new Date().toISOString().slice(0,10),
        location: s.location || s.address || ''
      }));
      setMutedStores(normalized);
      setTotal(meta.total || normalized.length);
      setPage(pg);
      setServerCap(caps);
    } catch (error) {
      console.error('Failed to fetch muted stores:', error);
      toast.error('ミュート済み店舗の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const unmuteStore = async (storeId) => {
    try {
      const target = mutedStores.find(s => s.id === storeId);
      if (!target) return;
      if (!target.employer_id) {
        toast.error('この店舗は解除に必要なID情報が不足しています');
        return;
      }
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      // 楽観更新
      setSavingIds(prev => new Set(prev).add(storeId));
      const prevList = mutedStores;
      setMutedStores(prev => prev.filter(store => store.id !== storeId));
      const paths = [
        `/users/muted-stores/${encodeURIComponent(target.employer_id)}`,
        `/user/muted-stores/${encodeURIComponent(target.employer_id)}`,
        `/muted-stores/${encodeURIComponent(target.employer_id)}`,
      ];
      let ok = false;
      for (const p of paths) {
        try {
          const res = await client.delete(p);
          if (res && res.status >= 200 && res.status < 300) { ok = true; break; }
        } catch (_) { /* try next */ }
      }
      if (!ok) {
        // rollback
        setMutedStores(prevList);
        throw new Error('解除に失敗しました');
      }
      toast.success('店舗のミュートを解除しました');
    } catch (error) {
      toast.error('ミュート解除に失敗しました');
    } finally {
      setSavingIds(prev => { const n = new Set(prev); n.delete(storeId); return n; });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReasonColor = (reason) => {
    switch (reason) {
      case 'inappropriate_behavior':
        return 'bg-red-100 text-red-800';
      case 'spam_notifications':
        return 'bg-yellow-100 text-yellow-800';
      case 'poor_working_conditions':
        return 'bg-orange-100 text-orange-800';
      case 'payment_issues':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  useEffect(() => {
    // サーバー検索が可能ならデバウンスしてサーバーに問い合わせ、なければクライアント検索
    if (serverCap.search) {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchMutedStores({ page: 1, q: searchTerm });
      }, 300);
    }
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchTerm, serverCap.search]);

  const filteredStores = serverCap.search ? mutedStores : (
    mutedStores.filter(store =>
      store.employer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      store.location.toLowerCase().includes(searchTerm.toLowerCase())
    )
  );

  // クライアントページング（サーバー未対応時）
  const clientPaged = serverCap.pagination ? filteredStores : (
    filteredStores.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize)
  );
  const effectiveTotal = serverCap.pagination ? total : filteredStores.length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">ミュートしている店舗</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 検索バー */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="店舗名や場所で検索..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* ミュート済み店舗一覧 */}
        <div className="space-y-4">
          {filteredStores.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center">
              {mutedStores.length === 0 ? (
                <>
                  <BuildingOfficeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">ミュートしている店舗はありません</h3>
                  <p className="text-gray-600 mb-6">
                    問題のある店舗や求人をミュートすることで、その店舗からの通知や求人表示を制限できます。
                  </p>
                </>
              ) : (
                <>
                  <MagnifyingGlassIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">検索結果が見つかりませんでした</h3>
                  <p className="text-gray-600">検索条件を変更してお試しください。</p>
                </>
              )}
            </div>
          ) : (
            clientPaged.map((store) => (
              <div key={store.id} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <BuildingOfficeIcon className="w-5 h-5 text-gray-400 mr-2" />
                      <h3 className="text-lg font-semibold text-gray-900">{store.employer_name}</h3>
                    </div>
                    
                    <p className="text-gray-600 mb-3">{store.location}</p>
                    
                    <div className="flex items-center space-x-4 mb-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getReasonColor(store.reason)}`}>
                        {store.reason_text}
                      </span>
                      <span className="text-sm text-gray-500">
                        ミュート日: {formatDate(store.muted_date)}
                      </span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => unmuteStore(store.id)}
                    disabled={savingIds.has(store.id)}
                    className={`ml-4 text-white px-4 py-2 rounded-lg transition-colors flex items-center ${savingIds.has(store.id) ? 'bg-blue-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                  >
                    <XMarkIcon className="w-4 h-4 mr-2" />
                    ミュート解除
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {effectiveTotal > pageSize && (
          <div className="mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {Math.min((page - 1) * pageSize + 1, effectiveTotal)} - {Math.min(page * pageSize, effectiveTotal)} / {effectiveTotal}
            </div>
            <div className="space-x-2">
              <button
                onClick={() => serverCap.pagination ? fetchMutedStores({ page: Math.max(1, page - 1), q: searchTerm }) : setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className={`px-3 py-1 rounded border ${page <= 1 ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              >
                前へ
              </button>
              <button
                onClick={() => {
                  const maxPage = Math.ceil(effectiveTotal / pageSize);
                  const next = Math.min(maxPage, page + 1);
                  if (serverCap.pagination) {
                    fetchMutedStores({ page: next, q: searchTerm });
                  } else {
                    setPage(next);
                  }
                }}
                disabled={page >= Math.ceil(effectiveTotal / pageSize)}
                className={`px-3 py-1 rounded border ${page >= Math.ceil(effectiveTotal / pageSize) ? 'text-gray-400 border-gray-200' : 'text-gray-700 border-gray-300 hover:bg-gray-50'}`}
              >
                次へ
              </button>
            </div>
          </div>
        )}

        {/* ミュート機能について */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <div className="flex items-start">
            <InformationCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-2">ミュート機能について</h4>
              <ul className="text-blue-800 text-sm space-y-1">
                <li> ミュートした店舗からの求人は検索結果に表示されません</li>
                <li> プッシュ通知やメール通知も停止されます</li>
                <li> いつでもミュートを解除できます</li>
                <li> ミュートしても過去の勤務履歴には影響しません</li>
                <li> 店舗側にはミュートしたことは通知されません</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MutedStores;